import type { FC } from "react";
import { useEffect } from "react";
import { useBoolean } from "usehooks-ts";

import { OUTCOME_ID_QUERY_PARAM, TAB_QUERY_PARAM } from "components/constants";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { AddOutcome } from "components/outcomes/AddOutcome";
import { displayOutcomeName, outcomesToOptions } from "components/outcomes/helpers";
import { t } from "i18n/i18n";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { ListItem } from "mds/components/ListItem";
import { useCapabilities } from "store/capability-hooks";
import { storeApi } from "store/index";
import {
  NestedSectionOutcomeType,
  outcomeSelectors,
  selectAssessedPageOutcomesBySectionOutcome,
  selectFullSectionOutcomes,
  selectPagesForOutcome,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { ModelType } from "utils/type-utils";
import { CourseContentTabKeys, withQueryParams } from "utils/urls";

const handleDeleteOutcome = (sectionOutcome: ModelType) => {
  trackEvent("Course - Delete outcome", {
    ...BUTTON_EVENT_CATEGORY,
  });
  return storeApi.section_outcomes.destroy(sectionOutcome.id);
};

type SectionOutcomeSidebarOptionsMenuProps = {
  sectionOutcome: Pick<NestedSectionOutcomeType, "id" | "outcome">;
};

const SectionOutcomeSidebarOptionsMenu: FC<SectionOutcomeSidebarOptionsMenuProps> = ({
  sectionOutcome,
}) => {
  const { canAuthorCourse } = useCapabilities();

  const assessedPageOutcomes = useListSelector((s) =>
    selectAssessedPageOutcomesBySectionOutcome(s, sectionOutcome.id),
  );
  const pagesForOutcome = useListSelector(
    (s) => selectPagesForOutcome(s, sectionOutcome.id),
    ["category"],
  );
  const topicPages = pagesForOutcome.filter((p) => p.category === "activity_page");
  const assignmentPages = pagesForOutcome.filter((p) => p.category === "assignment");
  const resourcePages = pagesForOutcome.filter((p) => p.category === "course_resource");

  const pageUsageCount = topicPages.length + assignmentPages.length + resourcePages.length;

  const { value: dialogIsOpen, setTrue: openDialog, setFalse: closeDialog } = useBoolean(false);

  if (!canAuthorCourse) {
    return null;
  }

  return (
    <>
      <SidebarOptionsMenu
        optionsMenuActions={{
          onDelete: pageUsageCount > 0 ? openDialog : () => handleDeleteOutcome(sectionOutcome),
          isDeleteDisabled: assessedPageOutcomes.length > 0,
          deleteTooltipText:
            assessedPageOutcomes.length > 0
              ? t("tooltip.cannot_delete_assessed_section_outcome")
              : undefined,
        }}
      />
      <ConfirmDeletionDialog
        glossaryKey="outcome"
        instanceName={sectionOutcome.outcome.title}
        open={dialogIsOpen}
        onClose={closeDialog}
        onDelete={() => handleDeleteOutcome(sectionOutcome)}
      >
        <div className="mt-4 border-l-2 border-red-tint-20 pl-4 font-semibold">
          {t("confirm_delete_modal.delete_warning_usage", { count: pageUsageCount })}
        </div>
      </ConfirmDeletionDialog>
    </>
  );
};

type OutcomesNavSidebarProps = {
  selectedOutcome?: string;
  sectionId: string;
};

export const OutcomesNavSidebar: FC<OutcomesNavSidebarProps> = ({ selectedOutcome, sectionId }) => {
  const { canAuthorSection, canAuthorCourse } = useCapabilities();

  // "outcome" is a nested object (deep-compared via isEqual). Fine here — outcomes rarely change.
  const sectionOutcomes = useListSelector(selectFullSectionOutcomes, [
    "id",
    "outcome_id",
    "outcome",
  ]);
  const orgOutcomes = useListSelector(outcomeSelectors.selectAll, ["id", "title"]);
  // TODO: When course-only outcomes are implemented, we should add it to this list
  const filteredSectionOutcomes = sectionOutcomes.filter(
    (sectionOutcome) => !!sectionOutcome.outcome,
  );

  const sectionOutcomeIdSet = new Set(
    sectionOutcomes.map((sectionOutcome) => sectionOutcome.outcome_id),
  );
  const selectableOutcomes = orgOutcomes.filter(
    (orgOutcome) => !sectionOutcomeIdSet.has(orgOutcome.id),
  );

  const options = outcomesToOptions(selectableOutcomes);

  useEffect(() => {
    // Only course authors need the section's outcome assessments, to gate deleting an assessed
    // outcome via the options menu. Students get their average outcome score from the server.
    if (canAuthorCourse) {
      storeApi.outcome_assessments.list({ section_id: sectionId });
    }
  }, [sectionId, canAuthorCourse]);

  return (
    <div className="w-full border-r border-black-tint-95 bg-white px-4 py-3 md:w-auto md:basis-[240px]">
      {!canAuthorSection && filteredSectionOutcomes.length === 0 && (
        <div className="body-s w-full py-2 text-black-tint-40">
          {t("outcomes.no_section_outcomes")}
        </div>
      )}
      {filteredSectionOutcomes.map((sectionOutcome) => (
        <ListItem
          className="w-full pl-0"
          isSelected={sectionOutcome.outcome.title === selectedOutcome}
          key={sectionOutcome.outcome.title}
          to={withQueryParams("", {
            [TAB_QUERY_PARAM]: CourseContentTabKeys[1],
            [OUTCOME_ID_QUERY_PARAM]: sectionOutcome.outcome.id,
          })}
        >
          <HoverableToolbar
            className="w-full"
            extraListItems={
              <li>
                <SectionOutcomeSidebarOptionsMenu sectionOutcome={sectionOutcome} />
              </li>
            }
            readOnly={!canAuthorSection}
            disableUnlessEditMode
          >
            <div
              aria-label={`${t("glossary.outcome")} ${displayOutcomeName(sectionOutcome.outcome)}`}
              className="body-s my-1 w-full cursor-pointer truncate px-2"
              role="listitem"
            >
              {displayOutcomeName(sectionOutcome.outcome)}
            </div>
          </HoverableToolbar>
        </ListItem>
      ))}

      {canAuthorSection && canAuthorCourse && (
        <AddOutcome
          options={options}
          hideUnlessEditMode
          onSave={(orgOutcomeIds: string[]) => {
            orgOutcomeIds.forEach((orgOutcomeId) => {
              storeApi.section_outcomes // TODO: Bulk create
                .create({ section_id: sectionId, outcome_id: orgOutcomeId });
            });
          }}
        />
      )}
    </div>
  );
};
